(() => {
  'use strict';

  const STARTED_AT = performance.now();
  const RUNTIME_VERSION = '0.5.0';
  const PANEL_ID = 'framewerk-devtools';
  const LOADER_SELECTOR = 'script[data-framewerk-loader]';
  const MAX_EVENTS = 150;
  const MAX_LOGS = 200;
  const bus = new EventTarget();
  const plugins = new Map();
  const mounts = new Map();
  const events = [];
  let eventSequence = 0;
  const logs = [];
  let developerMode = localStorage.getItem('framewerk:developer-mode') === '1';
  let runtime;

  const clean = (value, fallback = 'Unknown') =>
    typeof value === 'string' && value.trim() ? value.trim() : fallback;

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const serialise = (value) => {
    try { return JSON.stringify(value ?? {}); }
    catch { return '[unserialisable detail]'; }
  };

  const formatTime = (date) => new Intl.DateTimeFormat('en-AU', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(date);

  const getLoader = () => document.querySelector(LOADER_SELECTOR);
  const environmentLabel = (shop, role) => {
    if (role === 'Theme editor') return 'Theme editor';
    if (/framewerk-|development|dev-store/i.test(shop)) return 'Development';
    return 'Production';
  };

  function slotElements() {
    return [...document.querySelectorAll('[data-framewerk-slot]')];
  }

  function slotState(element) {
    const name = clean(element.dataset.framewerkSlot);
    const pluginIds = [...element.querySelectorAll(':scope > [data-framewerk-plugin]')]
      .map((node) => clean(node.dataset.framewerkPlugin, 'unknown'));
    return {
      name,
      ready: true,
      hidden: element.hidden,
      plugins: pluginIds,
      mountedChildren: element.children.length,
      element,
    };
  }

  function detect() {
    const loader = getLoader();
    const slots = slotElements().map(slotState);
    const shop = clean(loader?.dataset.shopDomain);
    const role = clean(loader?.dataset.themeRole, 'Storefront');
    const errors = logs.filter((entry) => entry.level === 'error').length;
    const warnings = logs.filter((entry) => entry.level === 'warn').length;
    const missingHeader = !document.querySelector('#header-group, #framewerk-header, header');
    const missingHeaderSlot = !slots.some((slot) => slot.name === 'header');
    const penalties = errors * 12 + warnings * 3 + (missingHeader ? 25 : 0) + (missingHeaderSlot ? 20 : 0);

    return {
      connected: Boolean(loader),
      runtimeVersion: RUNTIME_VERSION,
      integrationVersion: clean(loader?.dataset.framewerkIntegrationVersion),
      theme: clean(loader?.dataset.framewerkTheme),
      themeVersion: clean(loader?.dataset.framewerkThemeVersion),
      shop,
      shopName: clean(loader?.dataset.shopName),
      environment: environmentLabel(shop, role),
      context: role,
      template: clean(loader?.dataset.template, document.body.dataset.template || 'Unknown'),
      header: !missingHeader,
      slots,
      pluginCount: plugins.size,
      mountCount: mounts.size,
      appBlock: Boolean(document.querySelector('[data-framewerk-plugin], [data-framewerk-selector], [data-framewerk-fitment]')),
      appEmbed: Boolean(document.querySelector('[data-framewerk-engine], [data-mister-fitment-engine]')),
      developerMode,
      warnings,
      errors,
      health: Math.max(0, Math.min(100, 100 - penalties)),
      uptimeMs: performance.now() - STARTED_AT,
    };
  }

  function recordEvent(name, detail = {}, source = 'runtime', durationMs = 0) {
    const entry = { id: ++eventSequence, name, detail, source, durationMs, at: new Date() };
    events.unshift(entry);
    if (events.length > MAX_EVENTS) events.length = MAX_EVENTS;
    return entry;
  }

  function recordLog(level, message, data = null, source = 'runtime') {
    logs.unshift({ level, message, data, source, at: new Date() });
    if (logs.length > MAX_LOGS) logs.length = MAX_LOGS;
  }

  function emit(name, detail = {}, source = 'runtime') {
    const started = performance.now();
    bus.dispatchEvent(new CustomEvent(name, { detail: { ...detail, source } }));
    const entry = recordEvent(name, detail, source, performance.now() - started);
    runtime?.devtools?.refresh?.();
    return entry;
  }

  function writeLog(level, message, data = null, source = 'runtime') {
    recordLog(level, message, data, source);
    const method = ['error', 'warn', 'info', 'debug'].includes(level) ? level : 'log';
    console[method](`[Framewerk:${source}] ${message}`, data ?? '');
    runtime?.devtools?.refresh?.();
  }

  function normalisePlugin(plugin) {
    if (!plugin || typeof plugin.id !== 'string' || !plugin.id.trim()) {
      throw new TypeError('Framewerk plugin registration requires a non-empty id.');
    }

    return {
      id: plugin.id.trim(),
      name: clean(plugin.name, plugin.id.trim()),
      version: clean(plugin.version, '0.0.0'),
      description: clean(plugin.description, ''),
      mounts: Array.isArray(plugin.mounts) ? [...new Set(plugin.mounts)] : [],
      status: 'registered',
      health: 'healthy',
      warnings: [],
      registeredAt: new Date(),
      initialisedAt: null,
      instance: plugin,
      diagnostics: typeof plugin.diagnostics === 'function' ? plugin.diagnostics : null,
      capabilities: Array.isArray(plugin.capabilities) ? [...new Set(plugin.capabilities)] : [],
      eventsEmitted: 0,
      eventsReceived: 0,
    };
  }

  function publicPlugin(record) {
    return {
      id: record.id,
      name: record.name,
      version: record.version,
      description: record.description,
      mounts: [...record.mounts],
      status: record.status,
      health: record.health,
      warnings: [...record.warnings],
      registeredAt: record.registeredAt,
      initialisedAt: record.initialisedAt,
      diagnostics: record.diagnostics ? safelyRunDiagnostics(record) : null,
      capabilities: [...record.capabilities],
      uptimeMs: record.initialisedAt ? Date.now() - record.initialisedAt.getTime() : 0,
      eventsEmitted: events.filter((entry) => entry.source === record.id).length,
    };
  }

  function safelyRunDiagnostics(record) {
    try { return record.diagnostics?.(runtime) ?? null; }
    catch (error) {
      writeLog('warn', `Diagnostics failed for ${record.name}`, error, record.id);
      return { error: error instanceof Error ? error.message : String(error) };
    }
  }

  async function register(plugin) {
    const record = normalisePlugin(plugin);
    if (plugins.has(record.id)) unregister(record.id);
    plugins.set(record.id, record);
    emit('plugin:registered', publicPlugin(record), record.id);
    writeLog('info', `Plugin registered: ${record.name}`, publicPlugin(record), record.id);

    if (typeof plugin.init === 'function') {
      try {
        record.status = 'initialising';
        await plugin.init(runtime);
        record.status = 'running';
        record.initialisedAt = new Date();
        emit('plugin:ready', publicPlugin(record), record.id);
      } catch (error) {
        record.status = 'error';
        record.health = 'error';
        record.warnings.push(error instanceof Error ? error.message : String(error));
        writeLog('error', `Plugin initialisation failed: ${record.name}`, error, record.id);
        emit('plugin:error', { id: record.id, message: record.warnings.at(-1) }, record.id);
      }
    } else {
      record.status = 'running';
      record.initialisedAt = new Date();
      emit('plugin:ready', publicPlugin(record), record.id);
    }

    return publicPlugin(record);
  }

  function unregister(id) {
    const record = plugins.get(id);
    if (!record) return false;
    [...mounts.values()].filter((mount) => mount.pluginId === id)
      .forEach((mount) => unmount(mount.slotName, id));
    try { record.instance.destroy?.(runtime); }
    catch (error) { writeLog('warn', `Plugin destroy failed: ${record.name}`, error, id); }
    plugins.delete(id);
    emit('plugin:unregistered', { id, name: record.name }, id);
    return true;
  }

  function mount(slotName, pluginId, content) {
    const slot = document.querySelector(`[data-framewerk-slot="${CSS.escape(slotName)}"]`);
    if (!slot) throw new Error(`Framewerk slot not found: ${slotName}`);
    if (!plugins.has(pluginId)) throw new Error(`Framewerk plugin not registered: ${pluginId}`);

    const key = `${slotName}:${pluginId}`;
    let host = slot.querySelector(`:scope > [data-framewerk-plugin="${CSS.escape(pluginId)}"]`);
    if (!host) {
      host = document.createElement('div');
      host.dataset.framewerkPlugin = pluginId;
      host.dataset.framewerkMount = slotName;
      slot.appendChild(host);
    }
    if (content instanceof Node) host.replaceChildren(content);
    else if (typeof content === 'string') host.innerHTML = content;
    else if (typeof content === 'function') {
      const result = content(host, runtime);
      if (result instanceof Node) host.replaceChildren(result);
      else if (typeof result === 'string') host.innerHTML = result;
    }

    slot.hidden = false;
    slot.classList.remove('framewerk-slot--empty');
    mounts.set(key, { slotName, pluginId, element: host, mountedAt: new Date() });
    emit('plugin:mounted', { slot: slotName, pluginId }, pluginId);
    return host;
  }

  function unmount(slotName, pluginId) {
    const key = `${slotName}:${pluginId}`;
    const mountRecord = mounts.get(key);
    if (!mountRecord) return false;
    mountRecord.element.remove();
    mounts.delete(key);
    const slot = document.querySelector(`[data-framewerk-slot="${CSS.escape(slotName)}"]`);
    if (slot && !slot.children.length) {
      slot.hidden = true;
      slot.classList.add('framewerk-slot--empty');
    }
    emit('plugin:unmounted', { slot: slotName, pluginId }, pluginId);
    return true;
  }

  function setDeveloperMode(enabled) {
    developerMode = Boolean(enabled);
    document.documentElement.classList.toggle('framewerk-developer-mode', developerMode);
    localStorage.setItem('framewerk:developer-mode', developerMode ? '1' : '0');
    slotElements().forEach((slot) => {
      slot.dataset.framewerkSlotLabel = clean(slot.dataset.framewerkSlot).toUpperCase();
    });
    emit('developer-mode:changed', { enabled: developerMode }, 'framewerk-devtools');
    runtime?.devtools?.refresh?.();
    return developerMode;
  }

  function healthChecks(status) {
    return [
      { id: 'runtime', label: 'Theme runtime', ok: status.connected, state: status.connected ? 'healthy' : 'error', reason: status.connected ? 'Loader connected.' : 'Framewerk loader was not found.', action: status.connected ? '' : 'Check the framewerk-loader render in layout/theme.liquid.' },
      { id: 'theme', label: 'Framewerk Theme', ok: status.theme === 'Framewerk Theme', state: status.theme === 'Framewerk Theme' ? 'healthy' : 'warning', reason: status.theme === 'Framewerk Theme' ? `Theme ${status.themeVersion} identified.` : 'Unexpected theme identity.', action: status.theme === 'Framewerk Theme' ? '' : 'Confirm the loader data-framewerk-theme value.' },
      { id: 'header', label: 'Header', ok: status.header, state: status.header ? 'healthy' : 'error', reason: status.header ? 'Header detected.' : 'Header could not be detected.', action: status.header ? '' : 'Confirm the active header section renders a header element.' },
      { id: 'header-slot', label: 'Header mount', ok: status.slots.some((slot) => slot.name === 'header'), state: status.slots.some((slot) => slot.name === 'header') ? 'healthy' : 'error', reason: status.slots.some((slot) => slot.name === 'header') ? 'Header slot is ready.' : 'Header mount point is missing.', action: status.slots.some((slot) => slot.name === 'header') ? '' : 'Install the Framewerk header slot snippet from Pass 2.' },
      { id: 'app-block', label: 'Theme app block', ok: status.appBlock, state: status.appBlock ? 'healthy' : 'idle', reason: status.appBlock ? 'A Framewerk app block is present.' : 'No Framewerk app block is currently mounted.', action: status.appBlock ? '' : 'Add a Framewerk plugin block when a storefront plugin is ready.' },
      { id: 'app-embed', label: 'App embed', ok: status.appEmbed, state: status.appEmbed ? 'healthy' : 'idle', reason: status.appEmbed ? 'A Framewerk app embed is active.' : 'No Framewerk app embed is detected.', action: status.appEmbed ? '' : 'Enable the app embed after connecting the Framewerk app.' },
    ];
  }

  function inspectSnapshot() {
    const status = detect();
    const checks = healthChecks(status);
    return {
      generatedAt: new Date(),
      status,
      checks,
      plugins: [...plugins.values()].map(publicPlugin),
      mounts: [...mounts.values()].map(({ element, ...entry }) => entry),
      slots: status.slots.map(({ element, ...slot }) => slot),
      events: [...events],
      logs: [...logs],
      summary: {
        state: checks.some((check) => check.state === 'error') ? 'attention' : checks.some((check) => check.state === 'warning') ? 'warning' : 'healthy',
        errors: checks.filter((check) => check.state === 'error').length + status.errors,
        warnings: checks.filter((check) => check.state === 'warning').length + status.warnings,
      },
    };
  }

  function row(label, value, tone = '') {
    return `<div class="framewerk-devtools__row"><span>${escapeHtml(label)}</span><strong class="${tone ? `is-${tone}` : ''}">${escapeHtml(value)}</strong></div>`;
  }

  function indicator(label, value, state = 'neutral') {
    return `<div class="framewerk-devtools__metric"><span class="framewerk-status-dot is-${state}"></span><div><small>${escapeHtml(label)}</small><strong>${escapeHtml(value)}</strong></div></div>`;
  }

  function systemView(status) {
    const snapshot = inspectSnapshot();
    const label = snapshot.summary.state === 'healthy' ? 'Healthy' : snapshot.summary.state === 'warning' ? 'Check warnings' : 'Attention required';
    const tone = snapshot.summary.state === 'healthy' ? 'good' : snapshot.summary.state === 'warning' ? 'warn' : 'bad';
    return `
      <div class="framewerk-inspector__hero is-${tone}">
        <span class="framewerk-status-dot is-${tone}"></span>
        <div><small>STOREFRONT</small><strong>${label}</strong><p>${snapshot.summary.state === 'healthy' ? 'All essential Framewerk checks passed.' : 'Inspector found something that needs attention.'}</p></div>
      </div>
      <div class="framewerk-devtools__metrics">
        ${indicator('Theme runtime', status.connected ? 'Connected' : 'Missing', status.connected ? 'good' : 'bad')}
        ${indicator('Environment', status.environment, status.environment === 'Production' ? 'good' : 'warn')}
        ${indicator('Plugins', String(status.pluginCount), status.pluginCount ? 'good' : 'neutral')}
        ${indicator('Developer mode', status.developerMode ? 'Enabled' : 'Disabled', status.developerMode ? 'warn' : 'neutral')}
      </div>
      <button type="button" class="framewerk-devtools__action" data-framewerk-developer-toggle>${status.developerMode ? 'Disable' : 'Enable'} developer mode</button>
      <div class="framewerk-devtools__list-title">Guided checks</div>
      <div class="framewerk-inspector__checks">${snapshot.checks.map((check) => `<button type="button" class="framewerk-inspector__check is-${check.state}" data-framewerk-check="${escapeHtml(check.id)}"><span class="framewerk-status-dot is-${check.state === 'healthy' ? 'good' : check.state === 'error' ? 'bad' : check.state === 'warning' ? 'warn' : 'neutral'}"></span><span><strong>${escapeHtml(check.label)}</strong><small>${escapeHtml(check.reason)}</small></span><b>›</b></button>`).join('')}</div>
      <details class="framewerk-devtools__details"><summary>Runtime details</summary>${row('Store', status.shopName)}${row('Context', status.context)}${row('Template', status.template)}${row('Runtime', status.runtimeVersion)}${row('Integration', status.integrationVersion)}${row('Shop domain', status.shop)}</details>`;
  }

  function themeView(status) {
    return `<div class="framewerk-devtools__section">
      ${row('Theme', status.theme, status.theme === 'Framewerk Theme' ? 'good' : '')}
      ${row('Theme version', status.themeVersion)}
      ${row('Header', status.header ? 'Detected' : 'Missing', status.header ? 'good' : 'bad')}
      ${row('App block', status.appBlock ? 'Detected' : 'Not detected', status.appBlock ? 'good' : '')}
      ${row('App embed', status.appEmbed ? 'Active' : 'Not detected', status.appEmbed ? 'good' : '')}
    </div>
    <div class="framewerk-devtools__list-title">Mount points</div>
    ${status.slots.length ? status.slots.map((slot) => `<button type="button" class="framewerk-devtools__slot" data-framewerk-inspect-slot="${escapeHtml(slot.name)}"><span>${escapeHtml(slot.name)}</span><strong>${slot.plugins.length ? `${slot.plugins.length} plugin(s)` : 'Ready'}</strong></button>`).join('') : '<p class="framewerk-devtools__empty">No mount points detected.</p>'}`;
  }

  function pluginView() {
    const list = [...plugins.values()];
    if (!list.length) return '<p class="framewerk-devtools__empty">No plugins registered.</p>';
    return list.map((record) => {
      const details = publicPlugin(record);
      const healthTone = details.health === 'healthy' ? 'good' : details.health === 'error' ? 'bad' : 'warn';
      return `<button type="button" class="framewerk-devtools__plugin-card" data-framewerk-inspect-plugin="${escapeHtml(details.id)}">
        <div class="framewerk-devtools__plugin-title"><div><strong>${escapeHtml(details.name)}</strong><small>${escapeHtml(details.id)}</small></div><span class="framewerk-pill is-${healthTone}">${escapeHtml(details.status)}</span></div>
        <div class="framewerk-devtools__plugin-meta"><span>v${escapeHtml(details.version)}</span><span>${escapeHtml(details.mounts.join(', ') || 'No mounts')}</span><span>${details.eventsEmitted} events</span></div>
        ${details.description ? `<p>${escapeHtml(details.description)}</p>` : ''}
        ${details.warnings.length ? `<p class="is-warning">${escapeHtml(details.warnings.join(' · '))}</p>` : ''}
      </button>`;
    }).join('');
  }

  function eventView() {
    if (!events.length) return '<p class="framewerk-devtools__empty">No runtime events recorded.</p>';
    return events.map((entry) => `<button type="button" class="framewerk-devtools__stream-item" data-framewerk-inspect-event="${entry.id}"><time>${formatTime(entry.at)}</time><div><strong>${escapeHtml(entry.name)}</strong><small>${escapeHtml(entry.source)} · ${entry.durationMs.toFixed(2)} ms</small></div><code>${escapeHtml(serialise(entry.detail))}</code></button>`).join('');
  }

  function performanceView(status) {
    const nav = performance.getEntriesByType('navigation')[0];
    const resources = performance.getEntriesByType('resource').filter((item) => /framewerk-foundation\.(js|css)/.test(item.name));
    const bytes = resources.reduce((sum, item) => sum + (item.transferSize || 0), 0);
    return `<div class="framewerk-devtools__section">
      ${row('Runtime age', `${Math.round(status.uptimeMs)} ms`)}
      ${row('DOM interactive', nav ? `${Math.round(nav.domInteractive)} ms` : 'Unavailable')}
      ${row('Page load', nav?.loadEventEnd ? `${Math.round(nav.loadEventEnd)} ms` : 'In progress')}
      ${row('Framewerk assets', String(resources.length))}
      ${row('Transferred', bytes ? `${Math.round(bytes / 1024)} KB` : 'Cached / unavailable')}
      ${row('Plugins', String(status.pluginCount))}
      ${row('Active mounts', String(status.mountCount))}
      ${row('Events captured', String(events.length))}
    </div>`;
  }

  function logView() {
    if (!logs.length) return '<p class="framewerk-devtools__empty">No logs recorded.</p>';
    return logs.map((entry) => `<div class="framewerk-devtools__stream-item is-${escapeHtml(entry.level)}"><time>${formatTime(entry.at)}</time><div><strong>${escapeHtml(entry.level.toUpperCase())}</strong><small>${escapeHtml(entry.source)}</small></div><span>${escapeHtml(entry.message)}</span></div>`).join('');
  }

  function inspectSlot(name) {
    const slot = slotElements().map(slotState).find((item) => item.name === name);
    if (!slot) return;
    const panel = document.getElementById(PANEL_ID);
    const content = panel?.querySelector('.framewerk-devtools__content');
    if (!content) return;
    content.innerHTML = `<button type="button" class="framewerk-devtools__back" data-framewerk-back>← Back to Theme</button><div class="framewerk-devtools__section">${row('Mount', slot.name)}${row('Status', 'Ready', 'good')}${row('Plugins', slot.plugins.join(', ') || 'None')}${row('Child nodes', String(slot.mountedChildren))}${row('Hidden', slot.hidden ? 'Yes' : 'No')}</div>`;
    content.querySelector('[data-framewerk-back]')?.addEventListener('click', () => {
      panel.dataset.activeTab = 'theme';
      panel.refresh();
    });
  }

  function showDetail(title, body, backTab = 'system') {
    const panel = document.getElementById(PANEL_ID);
    const content = panel?.querySelector('.framewerk-devtools__content');
    if (!content) return;
    content.innerHTML = `<button type="button" class="framewerk-devtools__back" data-framewerk-back>← Back</button><div class="framewerk-inspector__detail"><p class="framewerk-devtools__list-title">${escapeHtml(title)}</p>${body}</div>`;
    content.querySelector('[data-framewerk-back]')?.addEventListener('click', () => { panel.dataset.activeTab = backTab; panel.refresh(); });
  }

  function inspectCheck(id) {
    const check = healthChecks(detect()).find((item) => item.id === id);
    if (!check) return;
    const tone = check.state === 'healthy' ? 'good' : check.state === 'error' ? 'bad' : check.state === 'warning' ? 'warn' : '';
    showDetail(check.label, `${row('Status', check.state === 'healthy' ? 'Healthy' : check.state === 'idle' ? 'Not active yet' : 'Attention required', tone)}${row('Reason', check.reason)}${check.action ? `<div class="framewerk-inspector__suggestion"><small>SUGGESTED ACTION</small><strong>${escapeHtml(check.action)}</strong></div>` : ''}`, 'system');
  }

  function inspectPlugin(id) {
    const record = plugins.get(id);
    if (!record) return;
    const details = publicPlugin(record);
    showDetail(details.name, `${row('Status', details.status, details.health === 'healthy' ? 'good' : 'bad')}${row('Version', details.version)}${row('Plugin ID', details.id)}${row('Uptime', `${Math.round(details.uptimeMs / 1000)} sec`)}${row('Mounts', details.mounts.join(', ') || 'None')}${row('Events emitted', String(details.eventsEmitted))}${row('Capabilities', details.capabilities.join(', ') || 'None')}${details.diagnostics ? `<details class="framewerk-devtools__details" open><summary>Plugin diagnostics</summary><pre class="framewerk-inspector__code">${escapeHtml(serialise(details.diagnostics))}</pre></details>` : ''}`, 'plugins');
  }

  function inspectEvent(id) {
    const entry = events.find((item) => item.id === Number(id));
    if (!entry) return;
    showDetail(entry.name, `${row('Time', formatTime(entry.at))}${row('Source', entry.source)}${row('Dispatch time', `${entry.durationMs.toFixed(2)} ms`)}<details class="framewerk-devtools__details" open><summary>Payload</summary><pre class="framewerk-inspector__code">${escapeHtml(JSON.stringify(entry.detail, null, 2))}</pre></details><button type="button" class="framewerk-devtools__action" data-framewerk-replay-event="${entry.id}">Replay event</button>`, 'events');
    document.querySelector(`[data-framewerk-replay-event="${entry.id}"]`)?.addEventListener('click', () => {
      emit(entry.name, { ...entry.detail, replayedFrom: entry.id }, 'framewerk-inspector');
      writeLog('info', `Event replayed: ${entry.name}`, { originalEventId: entry.id }, 'framewerk-inspector');
    });
  }

  function createDevToolsPlugin() {
    let panel;
    let render;
    return {
      id: 'framewerk-devtools',
      name: 'Framewerk Inspector',
      version: RUNTIME_VERSION,
      description: 'Self-describing runtime inspector, guided health checks, event replay and plugin diagnostics.',
      capabilities: ['overlay', 'runtime-inspection', 'plugin-inspection', 'event-viewer', 'event-replay', 'developer-mode'],
      mounts: ['modal'],
      init(api) {
        panel = document.getElementById(PANEL_ID) || document.createElement('aside');
        panel.id = PANEL_ID;
        panel.hidden = true;
        panel.dataset.activeTab = 'system';
        panel.setAttribute('aria-label', 'Framewerk Inspector');
        if (!panel.isConnected) document.body.appendChild(panel);

        render = () => {
          const status = api.detect();
          const active = panel.dataset.activeTab || 'system';
          const tabs = ['system', 'theme', 'plugins', 'events', 'performance', 'logs'];
          const views = {
            system: systemView(status), theme: themeView(status), plugins: pluginView(),
            events: eventView(), performance: performanceView(status), logs: logView(),
          };
          panel.innerHTML = `<header class="framewerk-devtools__header"><div><p>INSPECTOR</p><h2>FRAMEWERK</h2></div><button type="button" data-framewerk-close aria-label="Close DevTools">×</button></header><nav class="framewerk-devtools__tabs" aria-label="DevTools sections">${tabs.map((tab) => `<button type="button" data-framewerk-tab="${tab}" class="${active === tab ? 'is-active' : ''}">${tab}</button>`).join('')}</nav><div class="framewerk-devtools__content">${views[active]}</div><footer>Ctrl + Shift + F toggles Inspector</footer>`;

          panel.querySelector('[data-framewerk-close]')?.addEventListener('click', () => { panel.hidden = true; });
          panel.querySelectorAll('[data-framewerk-tab]').forEach((button) => button.addEventListener('click', () => { panel.dataset.activeTab = button.dataset.framewerkTab; render(); }));
          panel.querySelector('[data-framewerk-developer-toggle]')?.addEventListener('click', () => setDeveloperMode(!developerMode));
          panel.querySelectorAll('[data-framewerk-inspect-slot]').forEach((button) => button.addEventListener('click', () => inspectSlot(button.dataset.framewerkInspectSlot)));
          panel.querySelectorAll('[data-framewerk-check]').forEach((button) => button.addEventListener('click', () => inspectCheck(button.dataset.framewerkCheck)));
          panel.querySelectorAll('[data-framewerk-inspect-plugin]').forEach((button) => button.addEventListener('click', () => inspectPlugin(button.dataset.framewerkInspectPlugin)));
          panel.querySelectorAll('[data-framewerk-inspect-event]').forEach((button) => button.addEventListener('click', () => inspectEvent(button.dataset.framewerkInspectEvent)));
        };

        api.devtools = Object.freeze({
          open() { render(); panel.hidden = false; },
          close() { panel.hidden = true; },
          toggle() { render(); panel.hidden = !panel.hidden; },
          refresh() { if (!panel.hidden) render(); },
        });

        document.addEventListener('keydown', (event) => {
          if (!event.ctrlKey || !event.shiftKey || event.key.toLowerCase() !== 'f') return;
          event.preventDefault();
          api.devtools.toggle();
        });

        if (new URLSearchParams(location.search).get('framewerkdebug') === '1') api.devtools.open();
      },
      diagnostics: () => ({ open: panel ? !panel.hidden : false, developerMode, capturedEvents: events.length, capturedLogs: logs.length }),
    };
  }

  function initialise() {
    const base = {
      version: RUNTIME_VERSION,
      theme: Object.freeze({ name: 'Framewerk Theme', version: '1.0.0' }),
      detect,
      register,
      unregister,
      plugins: Object.freeze({
        register,
        unregister,
        get: (id) => plugins.has(id) ? publicPlugin(plugins.get(id)) : null,
        list: () => [...plugins.values()].map(publicPlugin),
      }),
      mounts: Object.freeze({
        mount,
        unmount,
        get: (slot, plugin) => mounts.get(`${slot}:${plugin}`) || null,
        list: () => [...mounts.values()].map(({ element, ...entry }) => entry),
      }),
      slots: Object.freeze({
        get: (name) => document.querySelector(`[data-framewerk-slot="${CSS.escape(name)}"]`),
        list: () => slotElements().map(slotState).map(({ element, ...slot }) => slot),
      }),
      on(name, callback, options) { bus.addEventListener(name, callback, options); return () => bus.removeEventListener(name, callback, options); },
      off(name, callback, options) { bus.removeEventListener(name, callback, options); },
      emit,
      log: Object.freeze({
        debug: (message, data, source) => writeLog('debug', message, data, source),
        info: (message, data, source) => writeLog('info', message, data, source),
        warn: (message, data, source) => writeLog('warn', message, data, source),
        error: (message, data, source) => writeLog('error', message, data, source),
        list: () => [...logs],
      }),
      diagnostics: Object.freeze({ status: detect, events: () => [...events], logs: () => [...logs], inspect: inspectSnapshot }),
      inspect: inspectSnapshot,
      manifest: Object.freeze({ principles: ['Theme owns presentation', 'Runtime owns lifecycle', 'Plugins communicate through Framewerk', 'Everything emits events', 'Everything is inspectable', 'Plugins are self-describing', 'Inspector uses the public API', 'No hidden magic', 'Small integrations, big capabilities', 'Never break the storefront'] }),
      developer: Object.freeze({ enabled: () => developerMode, set: setDeveloperMode, toggle: () => setDeveloperMode(!developerMode) }),
      devtools: null,
    };

    runtime = base;
    window.FramewerkTheme = runtime;
    if (!window.Framewerk?.vehicle?.subscribe) {
      window.Framewerk = runtime;
    }
    document.documentElement.classList.toggle('framewerk-developer-mode', developerMode);

    writeLog('info', `Framewerk Theme connected — runtime ${RUNTIME_VERSION}`, detect());
    register(createDevToolsPlugin());
    emit('runtime:ready', detect());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();
})();
